// Tour Interaction Helpers
// Functions to programmatically interact with UI elements during tours

// Re-export for backward compatibility (canonical implementations in tourNavigation.js)
export { waitForElement, scrollToElement } from './tourNavigation.js'

/**
 * Click an element (checkbox, button, etc.)
 * @param {string} selector - CSS selector for element
 * @param {number} delay - Optional delay before clicking (ms)
 */
export async function clickElement(selector, delay = 0) {
  if (delay > 0) {
    await new Promise(resolve => setTimeout(resolve, delay))
  }

  const element = document.querySelector(selector)
  if (element) {
    element.click()
    console.log(`[Tour] Clicked: ${selector}`)
    return true
  } else {
    console.warn(`[Tour] Element not found for click: ${selector}`)
    return false
  }
}

/**
 * Set value of a slider/range input
 * @param {string} selector - CSS selector for input
 * @param {number} value - Value to set
 */
export async function setSliderValue(selector, value) {
  const slider = document.querySelector(selector)
  if (slider) {
    slider.value = value
    // Trigger both input and change events
    slider.dispatchEvent(new Event('input', { bubbles: true }))
    slider.dispatchEvent(new Event('change', { bubbles: true }))
    console.log(`[Tour] Set slider ${selector} to ${value}`)
    return true
  } else {
    console.warn(`[Tour] Slider not found: ${selector}`)
    return false
  }
}

/**
 * Check/uncheck a checkbox
 * @param {string} selector - CSS selector for checkbox
 * @param {boolean} checked - True to check, false to uncheck
 */
export async function setCheckbox(selector, checked) {
  const checkbox = document.querySelector(selector)
  if (checkbox && checkbox.checked !== checked) {
    checkbox.click()
    console.log(`[Tour] Set checkbox ${selector} to ${checked}`)
    return true
  } else if (!checkbox) {
    console.warn(`[Tour] Checkbox not found: ${selector}`)
    return false
  }
  return true // Already in correct state
}

/**
 * Type text into an input field
 * @param {string} selector - CSS selector for input
 * @param {string} text - Text to type
 */
export async function typeText(selector, text) {
  const input = document.querySelector(selector)
  if (input) {
    input.value = text
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
    console.log(`[Tour] Typed "${text}" into ${selector}`)
    return true
  } else {
    console.warn(`[Tour] Input not found: ${selector}`)
    return false
  }
}

/**
 * Highlight an element temporarily
 * @param {string} selector - CSS selector
 * @param {number} duration - Duration in ms (default 2000)
 */
export async function pulseHighlight(selector, duration = 2000) {
  const element = document.querySelector(selector)
  if (element) {
    element.style.transition = 'box-shadow 0.3s ease'
    element.style.boxShadow = '0 0 0 3px rgba(234, 91, 12, 0.8), 0 0 20px rgba(234, 91, 12, 0.4)'

    await new Promise(resolve => setTimeout(resolve, duration))

    element.style.boxShadow = ''
    setTimeout(() => {
      element.style.transition = ''
    }, 300)

    console.log(`[Tour] Pulse highlighted: ${selector}`)
    return true
  } else {
    console.warn(`[Tour] Element not found for highlight: ${selector}`)
    return false
  }
}

/**
 * Simulate hovering over an element
 * @param {string} selector - CSS selector
 */
export async function hoverElement(selector) {
  const element = document.querySelector(selector)
  if (element) {
    const event = new MouseEvent('mouseenter', { bubbles: true })
    element.dispatchEvent(event)
    console.log(`[Tour] Hovered: ${selector}`)
    return true
  } else {
    console.warn(`[Tour] Element not found for hover: ${selector}`)
    return false
  }
}

/**
 * Remove hover from an element
 * @param {string} selector - CSS selector
 */
export async function unhoverElement(selector) {
  const element = document.querySelector(selector)
  if (element) {
    const event = new MouseEvent('mouseleave', { bubbles: true })
    element.dispatchEvent(event)
    console.log(`[Tour] Unhovered: ${selector}`)
    return true
  } else {
    console.warn(`[Tour] Element not found for unhover: ${selector}`)
    return false
  }
}
